import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, LightbulbIcon, CalendarIcon, ClockIcon } from 'lucide-react';
interface BlogPageProps {
  onNavigate: (page: string) => void;
}
function BlogHero({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <section className="relative w-full min-h-[55vh] flex items-end overflow-hidden" data-id="element-578">
      <div className="absolute inset-0" data-id="element-579">
        <img src="https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=1920&q=80" alt="Luxury trekking trail through the Himalayas at golden hour" className="w-full h-full object-cover" data-id="element-580" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" data-id="element-581" />
      </div>
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32" data-id="element-582">
        <nav className="mb-5" aria-label="Breadcrumb" data-id="element-583">
          <ol className="flex items-center gap-2 text-sm text-white/60" data-id="element-584">
            <li data-id="element-585">
              <button onClick={() => onNavigate('home')} className="hover:text-white transition-colors" data-id="element-586">
                Home
              </button>
            </li>
            <li data-id="element-587">/</li>
            <li data-id="element-588">
              <span className="hover:text-white transition-colors" data-id="element-589">Blog</span>
            </li>
            <li data-id="element-590">/</li>
            <li className="text-[#D4A853]" data-id="element-591">Luxury Trekking Guide</li>
          </ol>
        </nav>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="flex flex-wrap items-center gap-4 mb-5" data-id="element-592">
          <span className="px-3 py-1 bg-teal-600/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full tracking-wide" data-id="element-593">
            Trekking
          </span>
          <span className="flex items-center gap-1.5 text-white/60 text-sm" data-id="element-594">
            <CalendarIcon className="w-3.5 h-3.5" data-id="element-595" />
            January 8, 2026
          </span>
          <span className="flex items-center gap-1.5 text-white/60 text-sm" data-id="element-596">
            <ClockIcon className="w-3.5 h-3.5" data-id="element-597" />8 min read
          </span>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 max-w-4xl leading-tight" data-id="element-598">
          The Ultimate Guide to{' '}
          <span className="italic font-medium text-[#D4A853]" data-id="element-599">
            Luxury Trekking
          </span>{' '}
          in Nepal
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.15
      }} className="text-white/70 text-lg max-w-2xl font-light" data-id="element-600">
          Everything you need to know about experiencing the Himalayas in
          five-star comfort — from premium lodges to gourmet mountain dining.
        </motion.p>
      </div>
    </section>;
}
function ArticleBody() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <section className="w-full bg-white py-16 sm:py-24" ref={ref} data-id="element-601">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.7
    }} data-id="element-602">
        {/* Intro */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-603">
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-604">
            Nepal has long been the ultimate destination for trekkers seeking
            raw, untamed beauty. But in recent years, a quiet revolution has
            been transforming how discerning travelers experience the Himalayas.
            Luxury trekking — the art of combining world-class adventure with
            five-star comfort — has arrived in Nepal, and it's changing
            everything.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-605">
            Gone are the days when reaching Everest Base Camp meant enduring
            freezing teahouses, basic meals, and sleepless nights on thin
            mattresses. Today's luxury trekkers enjoy heated rooms, hot showers
            at altitude, gourmet cuisine prepared by trained chefs, and personal
            Sherpa guides who carry everything while you walk unburdened through
            the most spectacular scenery on Earth.
          </p>
        </div>

        {/* H2: Why Choose Luxury Trekking? */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-id="element-606">
          <div className="flex items-center gap-3 mb-6" data-id="element-607">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-608">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-609" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-610">◆</span>
          </div>
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-611">
            Why Choose Luxury Trekking?
          </h2>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-612">
            The appeal of luxury trekking goes far beyond mere comfort. It's
            about removing the barriers that prevent many people from
            experiencing the world's greatest landscapes. Age, fitness level,
            and tolerance for discomfort no longer need to be limiting factors.
            With luxury trekking, the Himalayas become accessible to anyone with
            the desire to see them.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-613">
            More importantly, luxury trekking allows you to be fully present in
            the moment. When you're not worrying about cold, hunger, or
            exhaustion, you can truly absorb the magnificence of your
            surroundings. You notice the play of light on Ama Dablam at sunset,
            the sound of prayer flags fluttering in the wind, the warmth of a
            Sherpa family's welcome.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-10" data-id="element-614">
            There's also a practical benefit: luxury trekkers tend to
            acclimatize better. Better sleep, better nutrition, and less
            physical stress all contribute to a safer, more enjoyable
            high-altitude experience. Our guides report significantly fewer
            altitude-related issues among luxury trekkers compared to standard
            groups.
          </p>
        </div>

        {/* Blockquote */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-id="element-615">
          <blockquote className="border-l-4 border-[#D4A853] pl-6 sm:pl-8 py-2" data-id="element-616">
            <p className="font-playfair text-xl sm:text-2xl text-slate-800 italic leading-relaxed mb-3" data-id="element-617">
              "Luxury trekking changed everything I thought I knew about
              adventure travel. You don't have to suffer to experience the
              extraordinary."
            </p>
            <cite className="text-slate-500 text-sm not-italic font-medium" data-id="element-618">
              — Sarah Mitchell, London, after her Everest Base Camp trek
            </cite>
          </blockquote>
        </div>

        {/* Full-width image */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" data-id="element-619">
          <figure data-id="element-620">
            <div className="rounded-2xl overflow-hidden" data-id="element-621">
              <img src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=1400&q=80" alt="Panoramic view of the Himalayan mountain range at sunrise" className="w-full aspect-[21/9] object-cover" data-id="element-622" />
            </div>
            <figcaption className="text-sm text-slate-400 mt-3 text-center italic" data-id="element-623">
              The Himalayan range at sunrise — a view that luxury trekkers enjoy
              from heated dining rooms over fresh coffee
            </figcaption>
          </figure>
        </div>

        {/* H2: What to Expect */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-624">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-8" data-id="element-625">
            What to Expect on a Luxury Trek
          </h2>

          {/* H3: Accommodation */}
          <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-slate-900 mb-4" data-id="element-626">
            Premium Accommodation
          </h3>
          <p className="text-slate-600 leading-relaxed text-base mb-8" data-id="element-627">
            Forget the image of cramped dormitories with paper-thin walls.
            Luxury lodges in the Khumbu and Annapurna regions offer private
            rooms with proper beds, thick duvets, and — crucially — heating
            systems that keep rooms comfortable even when temperatures outside
            plummet to -15°C. Many lodges feature attached bathrooms with hot
            showers, a luxury that standard trekkers can only dream of at
            altitude. The best lodges, like the Yeti Mountain Homes chain, are
            built in traditional Sherpa architectural style but with modern
            comforts seamlessly integrated.
          </p>

          {/* H3: Dining */}
          <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-slate-900 mb-4" data-id="element-628">
            Gourmet Mountain Dining
          </h3>
          <p className="text-slate-600 leading-relaxed text-base mb-8" data-id="element-629">
            One of the biggest surprises for luxury trekkers is the quality of
            food. While standard trekkers subsist on basic dal bhat and instant
            noodles, luxury treks feature multi-course meals prepared by trained
            chefs. Expect fresh soups, grilled meats, pasta dishes, and even
            desserts — all prepared with ingredients carried up by dedicated
            kitchen porters. Breakfasts include eggs cooked to order, fresh
            bread, pancakes, and real coffee. Dietary requirements including
            vegetarian, vegan, and gluten-free are accommodated with advance
            notice.
          </p>

          {/* H3: Guides */}
          <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-slate-900 mb-4" data-id="element-630">
            Expert Sherpa Guides
          </h3>
          <p className="text-slate-600 leading-relaxed text-base mb-10" data-id="element-631">
            Your Sherpa guide is the heart of the luxury trekking experience.
            These aren't just trail leaders — they're cultural ambassadors,
            storytellers, and mountain safety experts with decades of
            experience. On luxury treks, the guide-to-client ratio is typically
            1:4 or better, ensuring personalized attention. Guides carry
            satellite phones, supplemental oxygen, and comprehensive first-aid
            kits. They monitor your health daily, adjusting pace and schedule as
            needed. Many speak multiple languages fluently and hold
            international mountaineering certifications.
          </p>
        </div>

        {/* Image Grid */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" data-id="element-632">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-633">
            <figure className="rounded-xl overflow-hidden" data-id="element-634">
              <img src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=700&q=80" alt="Luxury mountain lodge interior with warm lighting" className="w-full aspect-[4/3] object-cover" data-id="element-635" />
              <figcaption className="text-sm text-slate-400 mt-2 italic" data-id="element-636">
                Premium lodge rooms with heated interiors
              </figcaption>
            </figure>
            <figure className="rounded-xl overflow-hidden" data-id="element-637">
              <img src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=700&q=80" alt="Gourmet mountain dining with fresh cuisine" className="w-full aspect-[4/3] object-cover" data-id="element-638" />
              <figcaption className="text-sm text-slate-400 mt-2 italic" data-id="element-639">
                Multi-course gourmet meals at altitude
              </figcaption>
            </figure>
            <figure className="rounded-xl overflow-hidden" data-id="element-640">
              <img src="https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=700&q=80" alt="Experienced Sherpa guide leading trekkers" className="w-full aspect-[4/3] object-cover" data-id="element-641" />
              <figcaption className="text-sm text-slate-400 mt-2 italic" data-id="element-642">
                Expert Sherpa guides with decades of experience
              </figcaption>
            </figure>
            <figure className="rounded-xl overflow-hidden" data-id="element-643">
              <img src="https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=700&q=80" alt="Dramatic Himalayan mountain scenery" className="w-full aspect-[4/3] object-cover" data-id="element-644" />
              <figcaption className="text-sm text-slate-400 mt-2 italic" data-id="element-645">
                Breathtaking scenery along the trekking route
              </figcaption>
            </figure>
          </div>
        </div>

        {/* H2: Best Routes */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-646">
          <div className="flex items-center gap-3 mb-6" data-id="element-647">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-648">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-649" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-650">◆</span>
          </div>
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-651">
            Best Routes for Luxury Trekking in Nepal
          </h2>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-652">
            The{' '}
            <strong className="text-slate-800" data-id="element-653">Everest Base Camp trek</strong>{' '}
            remains the crown jewel of luxury trekking in Nepal. The 14-day
            journey through the Khumbu Valley to the foot of the world's highest
            peak is now served by a chain of premium lodges that make the
            experience accessible without sacrificing the sense of achievement.
            The route passes through Namche Bazaar, Tengboche Monastery, and
            culminates at Base Camp (5,364m) with a sunrise excursion to Kala
            Patthar.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-654">
            The <strong className="text-slate-800" data-id="element-655">Annapurna Circuit</strong>{' '}
            offers a different kind of luxury experience. This 12-day trek
            circles the entire Annapurna massif, crossing the Thorong La pass at
            5,416m. Premium teahouse lodges along the route have been upgraded
            in recent years, and helicopter transfers can shorten the more
            demanding sections for those who prefer it.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-10" data-id="element-656">
            For something truly exclusive, the{' '}
            <strong className="text-slate-800" data-id="element-657">Upper Mustang expedition</strong>{' '}
            takes you into the former Kingdom of Lo — a restricted area that
            requires special permits. The landscape here is unlike anywhere else
            in Nepal: arid, wind-sculpted canyons and ancient cave monasteries
            that feel like stepping into another world. Luxury camping with
            heated tents and private chef service makes this remote adventure
            surprisingly comfortable.
          </p>
        </div>

        {/* YouTube Video */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" data-id="element-658">
          <div className="rounded-2xl overflow-hidden shadow-xl shadow-slate-200/50" data-id="element-659">
            <div className="relative w-full" style={{
            paddingBottom: '56.25%'
          }} data-id="element-660">
              <iframe src="https://www.youtube.com/embed/RVFXo3i6yBk" title="Luxury Trekking in Nepal — A Visual Journey" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-661" />
            </div>
          </div>
          <p className="text-sm text-slate-400 mt-3 text-center italic" data-id="element-662">
            Watch: Experience the magic of luxury trekking in the Himalayas
          </p>
        </div>

        {/* Pro Tips */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-14" data-id="element-663">
          <div className="bg-[#0C1F2C] rounded-2xl p-8 sm:p-10 border border-[#D4A853]/15" data-id="element-664">
            <div className="flex items-center gap-3 mb-6" data-id="element-665">
              <div className="w-10 h-10 bg-[#D4A853]/15 rounded-xl flex items-center justify-center" data-id="element-666">
                <LightbulbIcon className="w-5 h-5 text-[#D4A853]" data-id="element-667" />
              </div>
              <h3 className="font-playfair text-xl font-semibold text-white" data-id="element-668">
                Pro Tips for Luxury Trekking
              </h3>
            </div>
            <ul className="space-y-4" data-id="element-669">
              <li className="flex items-start gap-3" data-id="element-670">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-671" />
                <span className="text-slate-300 text-base" data-id="element-672">
                  <strong className="text-white" data-id="element-673">Book early</strong> — reserve
                  at least 3 months in advance for peak season
                  (October–November) as premium lodges fill up fast.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-674">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-675" />
                <span className="text-slate-300 text-base" data-id="element-676">
                  <strong className="text-white" data-id="element-677">Pack layers</strong> —
                  temperatures vary dramatically with altitude, from 25°C in
                  Kathmandu to -15°C at Base Camp.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-678">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-679" />
                <span className="text-slate-300 text-base" data-id="element-680">
                  <strong className="text-white" data-id="element-681">Bring a real camera</strong> —
                  phone batteries drain fast in cold temperatures. A compact
                  mirrorless camera with spare batteries is ideal.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-682">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-683" />
                <span className="text-slate-300 text-base" data-id="element-684">
                  <strong className="text-white" data-id="element-685">Tip your guides</strong> — it's
                  customary and deeply appreciated. Budget $15–20/day for your
                  lead guide and $10–15/day for porters.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-686">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-687" />
                <span className="text-slate-300 text-base" data-id="element-688">
                  <strong className="text-white" data-id="element-689">Train beforehand</strong> —
                  start cardiovascular training at least 2 months before your
                  trek. Hiking with a loaded daypack is the best preparation.
                </span>
              </li>
              <li className="flex items-start gap-3" data-id="element-690">
                <CheckIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-691" />
                <span className="text-slate-300 text-base" data-id="element-692">
                  <strong className="text-white" data-id="element-693">Get proper insurance</strong> —
                  ensure your travel insurance covers trekking up to 6,000m and
                  emergency helicopter evacuation.
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* H2: When to Go */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-694">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-6" data-id="element-695">
            When to Go
          </h2>
          <p className="text-slate-600 leading-relaxed text-base mb-6" data-id="element-696">
            Nepal's trekking seasons are defined by the monsoon. The two prime
            windows are{' '}
            <strong className="text-slate-800" data-id="element-697">
              autumn (September–November)
            </strong>{' '}
            and <strong className="text-slate-800" data-id="element-698">spring (March–May)</strong>.
            Autumn is widely considered the best time — the monsoon rains have
            cleared the air, leaving crystal-clear skies and the most
            spectacular mountain visibility of the year. Temperatures are
            comfortable at lower altitudes and manageable at higher ones.
          </p>
          <p className="text-slate-600 leading-relaxed text-base mb-10" data-id="element-699">
            Spring brings warmer temperatures and the added bonus of
            rhododendron forests in full bloom — hillsides painted in vivid
            reds, pinks, and whites. There's slightly more cloud cover than
            autumn, but the scenery is arguably even more beautiful. Winter
            trekking (December–February) is possible on luxury treks thanks to
            heated lodges, though the cold is intense and some higher passes may
            be closed. The monsoon months (June–August) are best avoided for
            trekking, though rain-shadow regions like Upper Mustang remain
            accessible.
          </p>
        </div>

        {/* Second Blockquote */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-id="element-700">
          <blockquote className="border-l-4 border-[#D4A853] pl-6 sm:pl-8 py-2" data-id="element-701">
            <p className="font-playfair text-xl sm:text-2xl text-slate-800 italic leading-relaxed mb-3" data-id="element-702">
              "The mountains are calling, and they're offering five-star
              service."
            </p>
            <cite className="text-slate-500 text-sm not-italic font-medium" data-id="element-703">
              — Nepal Luxury Travel
            </cite>
          </blockquote>
        </div>

        {/* Final full-width image */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-704">
          <figure data-id="element-705">
            <div className="rounded-2xl overflow-hidden" data-id="element-706">
              <img src="https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=1400&q=80" alt="Dramatic sunset over the Himalayan peaks with prayer flags" className="w-full aspect-[21/9] object-cover" data-id="element-707" />
            </div>
            <figcaption className="text-sm text-slate-400 mt-3 text-center italic" data-id="element-708">
              Sunset over the Himalayas — the perfect end to a day on the trail
            </figcaption>
          </figure>
        </div>
      </motion.div>
    </section>;
}
function RelatedTrips({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.12,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section className="w-full bg-[#0C1F2C] py-20 sm:py-28" ref={ref} data-id="element-709">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-710">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} className="text-center mb-14" data-id="element-711">
          <div className="flex items-center justify-center gap-3 mb-6" data-id="element-712">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-713">◆</span>
            <div className="w-12 h-0.5 bg-[#D4A853]" data-id="element-714" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-715">◆</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl font-bold text-white mb-3" data-id="element-716">
            You May Be Interested
          </h2>
          <p className="text-slate-400 font-light text-lg" data-id="element-717">
            Explore our most popular luxury adventures
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-718">
          {/* Everest Base Camp */}
          <motion.article custom={0} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} onClick={() => onNavigate('everest-trek')} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5 hover:shadow-2xl hover:shadow-teal-900/20 transition-all duration-500 cursor-pointer" data-id="element-719">
            <div className="relative overflow-hidden aspect-[16/9]" data-id="element-720">
              <img src="https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=900&q=80" alt="Mount Everest Base Camp trek" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-721" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-722">
                14 Days
              </span>
            </div>
            <div className="p-6 sm:p-7" data-id="element-723">
              <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-white mb-2 group-hover:text-[#D4A853] transition-colors" data-id="element-724">
                Everest Base Camp Luxury Trek
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-5" data-id="element-725">
                Trek to the foot of the world's highest peak with luxury lodges,
                gourmet dining, and personal Sherpa guides.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10" data-id="element-726">
                <span className="text-[#D4A853] text-lg font-semibold" data-id="element-727">
                  From $4,500
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 group-hover:text-[#D4A853] transition-colors" data-id="element-728">
                  View Details
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-729" />
                </span>
              </div>
            </div>
          </motion.article>

          {/* Annapurna Circuit */}
          <motion.article custom={1} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5 hover:shadow-2xl hover:shadow-teal-900/20 transition-all duration-500 cursor-pointer" data-id="element-730">
            <div className="relative overflow-hidden aspect-[16/9]" data-id="element-731">
              <img src="https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=900&q=80" alt="Annapurna mountain range panoramic view" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-732" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-733">
                12 Days
              </span>
            </div>
            <div className="p-6 sm:p-7" data-id="element-734">
              <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-white mb-2 group-hover:text-[#D4A853] transition-colors" data-id="element-735">
                Annapurna Circuit Premium
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-5" data-id="element-736">
                Circle the Annapurna massif with premium teahouse stays,
                helicopter transfers, and cultural immersion.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10" data-id="element-737">
                <span className="text-[#D4A853] text-lg font-semibold" data-id="element-738">
                  From $3,800
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 group-hover:text-[#D4A853] transition-colors" data-id="element-739">
                  View Details
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-740" />
                </span>
              </div>
            </div>
          </motion.article>

          {/* Chitwan Safari */}
          <motion.article custom={2} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5 hover:shadow-2xl hover:shadow-teal-900/20 transition-all duration-500 cursor-pointer" data-id="element-741">
            <div className="relative overflow-hidden aspect-[16/9]" data-id="element-742">
              <img src="https://images.unsplash.com/photo-1574950578143-858c6fc139d8?w=900&q=80" alt="Wildlife safari in Chitwan National Park" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-743" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-744">
                5 Days
              </span>
            </div>
            <div className="p-6 sm:p-7" data-id="element-745">
              <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-white mb-2 group-hover:text-[#D4A853] transition-colors" data-id="element-746">
                Chitwan Luxury Safari
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-5" data-id="element-747">
                Encounter rhinos, tigers, and exotic birds from the comfort of a
                five-star jungle lodge in Chitwan.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10" data-id="element-748">
                <span className="text-[#D4A853] text-lg font-semibold" data-id="element-749">
                  From $2,200
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 group-hover:text-[#D4A853] transition-colors" data-id="element-750">
                  View Details
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-751" />
                </span>
              </div>
            </div>
          </motion.article>

          {/* Upper Mustang */}
          <motion.article custom={3} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="group bg-[#132F3E] rounded-2xl overflow-hidden border border-white/5 hover:shadow-2xl hover:shadow-teal-900/20 transition-all duration-500 cursor-pointer" data-id="element-752">
            <div className="relative overflow-hidden aspect-[16/9]" data-id="element-753">
              <img src="https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=900&q=80" alt="Remote Upper Mustang landscape" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-754" />
              <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-755">
                10 Days
              </span>
            </div>
            <div className="p-6 sm:p-7" data-id="element-756">
              <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-white mb-2 group-hover:text-[#D4A853] transition-colors" data-id="element-757">
                Upper Mustang Expedition
              </h3>
              <p className="text-slate-400 text-base leading-relaxed mb-5" data-id="element-758">
                Explore the forbidden kingdom of Lo Manthang with exclusive
                access, luxury camping, and ancient monasteries.
              </p>
              <div className="flex items-center justify-between pt-4 border-t border-white/10" data-id="element-759">
                <span className="text-[#D4A853] text-lg font-semibold" data-id="element-760">
                  From $5,200
                </span>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 group-hover:text-[#D4A853] transition-colors" data-id="element-761">
                  View Details
                  <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-762" />
                </span>
              </div>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}
export function BlogPage({
  onNavigate
}: BlogPageProps) {
  return <div className="w-full" data-id="element-763">
      <BlogHero onNavigate={onNavigate} data-id="element-764" />
      <ArticleBody data-id="element-765" />
      <RelatedTrips onNavigate={onNavigate} data-id="element-766" />
    </div>;
}